import { EmailDesign, EmailBlock, TextBlockContent, ImageBlockContent, ButtonBlockContent, DividerBlockContent, SpacerBlockContent, HtmlBlockContent } from '../types';

/**
 * Parse HTML string into EmailDesign structure
 * This is a basic parser - for production, consider using a proper HTML parser library
 */
export function parseHtmlToDesign(html: string): EmailDesign {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  const blocks: EmailBlock[] = [];
  
  // Try to find the main content container
  const body = doc.body;
  const container = body.querySelector('table.email-container') || body.querySelector('table') || body;
  
  // Extract styles from the container
  const containerStyle = extractStyles(container as HTMLElement);
  
  // Parse blocks from the HTML
  const rows = container.querySelectorAll('tr');
  
  rows.forEach((row) => {
    const cells = row.querySelectorAll('td');
    cells.forEach((cell) => {
      const block = parseCellToBlock(cell as HTMLElement);
      if (block) {
        blocks.push(block);
      }
    });
  });
  
  // If no blocks found, create a text block with the HTML content
  if (blocks.length === 0) {
    blocks.push({
      id: `block-${Date.now()}`,
      type: 'html',
      content: {
        html: html
      } as HtmlBlockContent
    });
  }
  
  return {
    blocks,
    styles: {
      backgroundColor: containerStyle.backgroundColor || '#ffffff',
      width: containerStyle.width || '600px',
      maxWidth: containerStyle.maxWidth || '600px',
      fontFamily: containerStyle.fontFamily || 'Arial, sans-serif',
      fontSize: parseInt(containerStyle.fontSize) || 14,
      textColor: containerStyle.color || '#000000'
    }
  };
}

function parseCellToBlock(cell: HTMLElement): EmailBlock | null {
  const text = cell.textContent?.trim() || '';
  const innerHTML = cell.innerHTML.trim();
  
  // Check for images
  const img = cell.querySelector('img');
  if (img) {
    return {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      content: {
        src: img.getAttribute('src') || '',
        alt: img.getAttribute('alt') || '',
        width: img.style.width || '100%',
        height: img.style.height || 'auto',
        align: getTextAlign(cell),
        link: cell.querySelector('a')?.getAttribute('href') || undefined,
        borderRadius: img.style.borderRadius || '0'
      } as ImageBlockContent
    };
  }
  
  // Check for buttons/links styled as buttons
  const link = cell.querySelector('a') as HTMLAnchorElement | null;
  const linkStyle = link?.style;
  if (link && linkStyle && (linkStyle.backgroundColor || linkStyle.padding)) {
    return {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      content: {
        text: link.textContent || 'Click here',
        link: link.getAttribute('href') || '#',
        backgroundColor: linkStyle.backgroundColor || '#3b82f6',
        textColor: linkStyle.color || '#ffffff',
        fontSize: parseInt(linkStyle.fontSize) || 16,
        fontWeight: linkStyle.fontWeight || 'bold',
        padding: linkStyle.padding || '12px 24px',
        borderRadius: linkStyle.borderRadius || '4px',
        align: getTextAlign(cell)
      } as ButtonBlockContent
    };
  }
  
  // Check for dividers
  const divider = cell.querySelector('div[style*="border-top"], hr') as HTMLElement | null;
  if (divider) {
    const style = divider.getAttribute('style') || '';
    return {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'divider',
      content: {
        color: extractColorFromStyle(style, 'border-top') || '#e5e7eb',
        thickness: extractThicknessFromStyle(style) || 1,
        style: extractBorderStyle(style) || 'solid',
        spacing: cell.style.padding || '20px 0',
        width: divider.style?.width || '100%',
        align: getTextAlign(cell)
      } as DividerBlockContent
    };
  }
  
  // Check for spacers (empty cells with height)
  if (!text && cell.style.height) {
    const height = parseInt(cell.style.height) || 20;
    return {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'spacer',
      content: {
        height
      } as SpacerBlockContent
    };
  }
  
  // Default to text block
  if (text || innerHTML) {
    const cellStyle = cell.style;
    return {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'text',
      content: {
        text: text || innerHTML.replace(/<[^>]*>/g, ''),
        fontSize: parseInt(cellStyle.fontSize) || 16,
        fontFamily: cellStyle.fontFamily || 'Arial, sans-serif',
        fontWeight: cellStyle.fontWeight || 'normal',
        fontStyle: cellStyle.fontStyle || 'normal',
        color: cellStyle.color || '#000000',
        backgroundColor: cellStyle.backgroundColor || 'transparent',
        textAlign: getTextAlign(cell),
        lineHeight: cellStyle.lineHeight || '1.5',
        padding: cellStyle.padding || '10px',
        margin: cellStyle.margin || '0',
        link: link?.getAttribute('href') || undefined
      } as TextBlockContent
    };
  }
  
  return null;
}

function extractStyles(element: HTMLElement): Record<string, string> {
  const styles: Record<string, string> = {};
  const computed = window.getComputedStyle(element);
  
  ['backgroundColor', 'width', 'maxWidth', 'fontFamily', 'fontSize', 'color'].forEach(prop => {
    const value = computed.getPropertyValue(prop);
    if (value) {
      styles[prop] = value;
    }
  });
  
  return styles;
}

function getTextAlign(element: HTMLElement): 'left' | 'center' | 'right' {
  const align = element.style.textAlign || window.getComputedStyle(element).textAlign;
  if (align === 'center' || align === 'right') {
    return align;
  }
  return 'left';
}

function extractColorFromStyle(style: string, property: string): string | null {
  const match = style.match(new RegExp(`${property}:\\s*[^;]+`));
  if (match) {
    const colorMatch = match[0].match(/#[0-9a-fA-F]{6}|rgb\\([^)]+\\)|[a-z]+/);
    return colorMatch ? colorMatch[0] : null;
  }
  return null;
}

function extractThicknessFromStyle(style: string): number {
  const match = style.match(/border-top:\s*(\d+)px/);
  return match ? parseInt(match[1]) : 1;
}

function extractBorderStyle(style: string): 'solid' | 'dashed' | 'dotted' {
  if (style.includes('dashed')) return 'dashed';
  if (style.includes('dotted')) return 'dotted';
  return 'solid';
}
